import { petEquipmentMaxMana, type PetEquipment } from './petEquipmentService.ts';
import type { PetCompanion, PetMatureBenefit, PetMatureBenefitId } from '../domain/pet.ts';
import { petCanReceiveAdvancementPoint, petLevelsForCrawlerLevelUp, petMaxHealthSlots, petMaxMana } from '../domain/pet.ts';
import type { StatId } from '../domain/stats.ts';
import { manaRegenPerRound } from '../rules/mana.ts';
import { statModForScore } from '../rules/statModifiers.ts';

export type PetProgressionResult = { ok: true; pet: PetCompanion; levelsGained?: number } | { ok: false; reason: string };

export interface PetLevelUpAllocation {
  str: number;
  con: number;
  dex: number;
  cha: number;
}

export type PetMatureBenefitChoice =
  | { id: Exclude<PetMatureBenefitId, 'custom'> }
  | { id: 'custom'; customOptionId: string };

export interface PetPlayerAdvancementPreview {
  pendingCrawlerLevels: number;
  fromLevel: number;
  toLevel: number;
  levelsGained: number;
  pointsAvailable: number;
  requiresMatureBenefit: boolean;
  canAdvance: boolean;
}

/** One pet progression event caused by the owning crawler gaining one Level. */
export function advancePetForCrawlerLevel(pet: PetCompanion, now: string): PetProgressionResult {
  const levelsGained = petLevelsForCrawlerLevelUp(pet.level);
  if (levelsGained < 1) return { ok: false, reason: 'Pet level is invalid.' };
  const oldMaxHealth = petMaxHealthSlots(pet);
  const level = pet.level + levelsGained;
  const nextMaxHealth = petMaxHealthSlots({ ...pet, level });
  const newlyAvailableHealthSlots = Math.max(0, nextMaxHealth - oldMaxHealth);
  return {
    ok: true,
    levelsGained,
    pet: {
      ...pet,
      level,
      unspentStatPoints: pet.unspentStatPoints + levelsGained * 3,
      healthBarSlotsRemaining: Math.min(nextMaxHealth, pet.healthBarSlotsRemaining + newlyAvailableHealthSlots),
      updatedAt: now,
    },
  };
}

export function petPendingCrawlerLevels(pet: Pick<PetCompanion, 'ownerLevelLastAdvanced'>, crawlerLevel: number): number {
  if (!Number.isInteger(crawlerLevel) || crawlerLevel < 1) return 0;
  return Math.max(0, crawlerLevel - pet.ownerLevelLastAdvanced);
}

/**
 * Preview all pet progress currently owed by the crawler's authoritative Level.
 * Existing unspent Stat Points are intentionally included so old GM-managed
 * advancement can be finalized through the new one-way player flow.
 */
export function previewPlayerPetAdvancement(pet: PetCompanion, crawlerLevel: number): PetPlayerAdvancementPreview {
  const pendingCrawlerLevels = petPendingCrawlerLevels(pet, crawlerLevel);
  let simulated = pet;
  let levelsGained = 0;
  for (let i = 0; i < pendingCrawlerLevels; i += 1) {
    const out = advancePetForCrawlerLevel(simulated, simulated.updatedAt);
    if (!out.ok) break;
    levelsGained += out.levelsGained ?? 0;
    simulated = out.pet;
  }
  const requiresMatureBenefit = simulated.level >= 15 && pet.matureBenefit === null;
  const canAdvance = pendingCrawlerLevels > 0 || simulated.unspentStatPoints > 0 || requiresMatureBenefit;
  return {
    pendingCrawlerLevels,
    fromLevel: pet.level,
    toLevel: simulated.level,
    levelsGained,
    pointsAvailable: simulated.unspentStatPoints,
    requiresMatureBenefit,
    canAdvance,
  };
}

function matureBenefitFromChoice(pet: PetCompanion, choice: PetMatureBenefitChoice | null): PetMatureBenefit | null | string {
  if (!choice) return null;
  if (choice.id !== 'custom') return { id: choice.id, notes: '' };
  const option = pet.customMatureBenefits.find((entry) => entry.id === choice.customOptionId);
  if (!option) return 'That custom Level-15 benefit is no longer available. Reopen Level Up and choose again.';
  return {
    id: 'custom',
    notes: option.notes,
    customName: option.name,
    customOptionId: option.id,
  };
}

/**
 * Atomic player confirmation. Nothing is mutated until every point is assigned,
 * any required Level-15 benefit is selected, and the crawler Level still supports
 * the advancement being claimed.
 */
export function commitPlayerPetAdvancement(
  pet: PetCompanion,
  crawlerLevel: number,
  allocation: PetLevelUpAllocation,
  matureChoice: PetMatureBenefitChoice | null,
  now: string,
): PetProgressionResult {
  if (pet.healthBarSlotsRemaining <= 0) return { ok: false, reason: 'A dead pet cannot level up until the GM resolves its status.' };
  const preview = previewPlayerPetAdvancement(pet, crawlerLevel);
  if (!preview.canAdvance) return { ok: false, reason: 'This pet has no pending level-up, Stat Points, or maturity choice.' };

  const entries = Object.entries(allocation) as [keyof PetLevelUpAllocation, number][];
  for (const [stat, points] of entries) {
    if (!Number.isInteger(points) || points < 0) return { ok: false, reason: `${stat.toUpperCase()} allocation must be a whole number of at least 0.` };
  }
  const totalAllocated = entries.reduce((sum, [, points]) => sum + points, 0);
  if (totalAllocated !== preview.pointsAvailable) {
    return { ok: false, reason: `Allocate all ${preview.pointsAvailable} available pet Stat Points before confirming.` };
  }

  let advanced = pet;
  for (let i = 0; i < preview.pendingCrawlerLevels; i += 1) {
    const out = advancePetForCrawlerLevel(advanced, now);
    if (!out.ok) return out;
    advanced = out.pet;
  }

  let matureBenefit = advanced.matureBenefit;
  if (preview.requiresMatureBenefit) {
    if (!matureChoice) return { ok: false, reason: 'Choose the pet’s Level-15 maturity benefit before confirming.' };
    const selected = matureBenefitFromChoice(advanced, matureChoice);
    if (typeof selected === 'string') return { ok: false, reason: selected };
    matureBenefit = selected;
  }

  const unenhancedStats = {
    ...advanced.unenhancedStats,
    str: advanced.unenhancedStats.str + allocation.str,
    con: advanced.unenhancedStats.con + allocation.con,
    dex: advanced.unenhancedStats.dex + allocation.dex,
    cha: advanced.unenhancedStats.cha + allocation.cha,
  };
  return {
    ok: true,
    levelsGained: preview.levelsGained,
    pet: {
      ...advanced,
      unenhancedStats,
      unspentStatPoints: 0,
      ownerLevelLastAdvanced: Math.max(advanced.ownerLevelLastAdvanced, crawlerLevel),
      matureBenefit,
      updatedAt: now,
    },
  };
}

export function allocatePetStatPoints(pet: PetCompanion, stat: StatId, points: number, now: string): PetProgressionResult {
  if (!Number.isInteger(points) || points < 1) return { ok: false, reason: 'Stat Points must be a positive whole number.' };
  if (!petCanReceiveAdvancementPoint(stat)) return { ok: false, reason: 'Pet level-up Stat Points cannot increase Intelligence.' };
  if (pet.unspentStatPoints < points) return { ok: false, reason: `Only ${pet.unspentStatPoints} pet Stat Point(s) remain.` };
  return {
    ok: true,
    pet: {
      ...pet,
      unenhancedStats: { ...pet.unenhancedStats, [stat]: pet.unenhancedStats[stat] + points },
      unspentStatPoints: pet.unspentStatPoints - points,
      updatedAt: now,
    },
  };
}

export function setPetManaBonus(pet: PetCompanion, manaBonus: number, now: string): PetProgressionResult {
  if (!Number.isInteger(manaBonus) || manaBonus < 0) return { ok: false, reason: 'Mana bonus must be a whole number of at least 0.' };
  const next = { ...pet, manaBonus, updatedAt: now };
  return { ok: true, pet: { ...next, currentMana: Math.min(next.currentMana, petMaxMana(next)) } };
}

export function setPetCurrentMana(pet: PetCompanion, currentMana: number, now: string): PetProgressionResult {
  const max = petMaxMana(pet);
  if (!Number.isInteger(currentMana) || currentMana < 0 || currentMana > max) return { ok: false, reason: `Current Mana must be between 0 and ${max}.` };
  return { ok: true, pet: { ...pet, currentMana, updatedAt: now } };
}

export function restorePetMana(pet: PetCompanion, now: string, equipment?: PetEquipment): PetCompanion {
  return { ...pet, currentMana: petEquipmentMaxMana(pet, equipment), updatedAt: now };
}

/**
 * Standing project Mana rule applied to pets once per completed combat round.
 * Pets currently have no separate equipment/effect layer, so their effective
 * Intelligence modifier is derived from their stored Intelligence score.
 */
export function regeneratePetManaPerRound(pet: PetCompanion, now: string, equipment?: PetEquipment): PetCompanion {
  const max = petEquipmentMaxMana(pet, equipment);
  const regen = manaRegenPerRound(statModForScore(equipment?.enhancedStats.int ?? pet.unenhancedStats.int));
  const currentMana = Math.min(max, pet.currentMana + regen);
  return currentMana === pet.currentMana ? pet : { ...pet, currentMana, updatedAt: now };
}

export function spendPetMana(pet: PetCompanion, cost: number, now: string, equipment?: PetEquipment): PetProgressionResult {
  if (!Number.isInteger(cost) || cost < 0) return { ok: false, reason: 'Mana cost must be a whole number of at least 0.' };
  const current = Math.min(pet.currentMana, petEquipmentMaxMana(pet, equipment));
  if (current < cost) return { ok: false, reason: `This pet needs ${cost} Mana but has ${current}.` };
  return { ok: true, pet: { ...pet, currentMana: current - cost, updatedAt: now } };
}
